import { useEffect, useState } from "react";
import { ProductImage, sourceLabelFor } from "../components/ProductImage";
import { Button } from "../components/ui/button";
import { getFinds, getPublicFinds } from "../lib/api";
import { navigate } from "../lib/router";
import type { FindsCard } from "../lib/types";

const LIMIT = 20;

export function FindsRoute() {
  const [finds, setFinds] = useState<FindsCard[]>([]);
  const [publicFinds, setPublicFinds] = useState<FindsCard[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getFinds(page).then((myData) => {
      if (cancelled) return;
      if (myData) {
        setFinds(myData.finds);
        setTotal(myData.total);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [page]);

  useEffect(() => {
    let cancelled = false;
    getPublicFinds().then((data) => {
      if (cancelled || !data) return;
      setPublicFinds(data.finds);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <section
      id="finds-view"
      className="w-full px-4 pb-4 grid gap-[1.8rem] pt-[2.4rem] flex-1 min-h-0 overflow-auto content-start"
    >
      <div>
        <h2 className="m-0 font-bold tracking-[-0.04em] text-[clamp(1.4rem,3vw,2.2rem)]">
          Your Finds
        </h2>
      </div>

      {loading ? (
        <div className="flex items-center justify-center text-muted py-8">Loading…</div>
      ) : finds.length === 0 ? (
        <p className="text-muted">No finds yet.</p>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-4">
          {finds.map((find) => (
            <FindCard key={find.id} find={find} />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center gap-3 justify-center">
          <Button
            variant="ghost"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-muted">
            {page} / {totalPages}
          </span>
          <Button
            variant="ghost"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {publicFinds.length > 0 && (
        <>
          <div>
            <h2 className="m-0 font-bold tracking-[-0.04em] text-[clamp(1.4rem,3vw,2.2rem)]">
              Finds by Other Users
            </h2>
          </div>
          <div id="public-finds-list" className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-4">
            {publicFinds.map((find) => (
              <FindCard key={find.id} find={find} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function FindCard({ find }: { find: FindsCard }) {
  return (
    <article
      className="panel-surface rounded-[14px] overflow-hidden cursor-pointer"
      onClick={() => navigate(`/finds/${find.id}`)}
    >
      <ProductImage
        imageUrl={find.imageUrl}
        title={find.title}
        imageScale={find.imageScale}
        imageOffsetX={find.imageOffsetX}
        imageOffsetY={find.imageOffsetY}
      />
      <div className="p-3">
        <p className="font-medium text-sm leading-tight line-clamp-2">
          {find.title}
        </p>
        <p className="text-muted text-xs mt-1">
          {sourceLabelFor(find.url)}
        </p>
        {find.ownerUsername && (
          <p className="text-xs mt-1 text-muted">
            by @{find.ownerUsername}
          </p>
        )}
      </div>
    </article>
  );
}
